import { useCancelReservation } from "@/api/reservation/useCancelReservation";
import { QuestionnaireUILayout } from "@/components/shared/QuestionnaireUILayout";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import { useQuestionnaireFlow } from "@/hooks/useQuestionnaireFlow";
import { QuestionnaireFlowQuestion, QuestionnaireFlowQuestionOption } from "@/types/question.types";
import { Stack, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { QuestionAndAnswerResolver } from "../../PersonalityTest/QuestionAndAnswerResolver";

interface ReservationChangeQuestionnaireProps {
  questions: QuestionnaireFlowQuestion[];
  reservationId: string;
}

export function ReservationChangeQuestionnaire({
  questions,
  reservationId,
}: ReservationChangeQuestionnaireProps) {
  const navigate = useNavigate();
  const { mutateAsync: cancelReservation } = useCancelReservation();

  const form = useForm({
    initialValues: {
      cancellationReason: "",
    },
  });

  const getQuestionPaths = (question: QuestionnaireFlowQuestion) => {
    if (question.slug === "reservation-change-reason") {
      return {
        fieldPath: "cancellationReason",
        validationPath: "cancellationReason",
      };
    }
    return {
      fieldPath: question.slug,
      validationPath: question.slug,
    };
  };

  const {
    currentQuestion,
    questionAnalysis,
    isLastStep,
    progressValue,
    actions: { goToNextStep, goToPreviousStep },
  } = useQuestionnaireFlow({
    questions,
    getQuestionPaths,
  });

  const handleGoToPreviousStep = () => {
    if (currentQuestion.slug === "reservation-change") {
      navigate(-1);
      return;
    }
    goToPreviousStep();
  };

  const handleSelect = (option: QuestionnaireFlowQuestionOption) => {
    if (currentQuestion.slug === "reservation-change-reason") {
      form.setFieldValue("cancellationReason", option.value ?? "unknown");
    }
  };

  const resolveButtonText = () => {
    if (currentQuestion.slug === "reservation-change") {
      return "예약 취소";
    }
    if (currentQuestion.slug === "reservation-change-reason") {
      return "확인";
    }
    return "다음";
  };

  const handleNextButtonClick = () => {
    if (currentQuestion.slug === "reservation-change") {
      goToNextStep();
    }
  };

  const handleSubmitButtonClick = async () => {
    if (currentQuestion.slug === "reservation-change-reason") {
      try {
        await cancelReservation({
          reservationId: parseInt(reservationId),
          cancellationReason: form.values.cancellationReason,
        });
        goToNextStep();
      } catch (error) {
        console.error(error);
        notifications.show({
          title: "예약 취소 실패",
          message: "예약 취소에 실패했습니다.",
          color: "red",
        });
      }
    }
  };

  if (currentQuestion.slug === "reservation-cancel-confirmation") {
    return (
      <Stack justify="space-between" align="center" h="100%" pb="xl" pt="lg" px="xl">
        <div></div>
        <Stack>
          <Title ta="center" pb="5rem" c="yellow.0">
            다음에 꼭 다시 만나요!
          </Title>
          <Text ta="center">미리 알려주셔서 감사해요! ☺️</Text>
          <Text ta="center">환불 정책에 따라 3-7일 이내에 전액 환불될 예정이에요.</Text>
          <Text ta="center">
            이번에 함께 하지 못해 아쉽지만 다음에 다시 찾아주세요. 곧 만나뵐 수 있기를 기대하고
            있을게요! 🦄
          </Text>
        </Stack>
        <StyledActionButton onClick={() => navigate("/home")}>확인</StyledActionButton>
      </Stack>
    );
  }

  const questionAreaContent =
    currentQuestion.slug === "reservation-change" ? null : (
      <QuestionAndAnswerResolver
        currentQuestion={currentQuestion}
        form={form}
        fieldPath={currentQuestion.fieldPath}
        handleSelectMultiple={handleSelect}
        handleSelectSingle={handleSelect}
        handleSelectRange={() => {}}
      />
    );

  return (
    <QuestionnaireUILayout
      progressValue={progressValue}
      showBackButton={currentQuestion.slug !== "reservation-cancel-confirmation"}
      onBackButtonClick={handleGoToPreviousStep}
      title={currentQuestion.title}
      subtitle={currentQuestion.subtitle ?? undefined}
      questionAreaContent={questionAreaContent}
      showNextButton={currentQuestion.slug === "reservation-change"}
      onNextButtonClick={handleNextButtonClick}
      isNextButtonDisabled={false}
      showSubmitButton={currentQuestion.slug === "reservation-change-reason"}
      onSubmitButtonClick={handleSubmitButtonClick}
      isSubmitButtonDisabled={!form.values.cancellationReason}
      pbForQuestionTitle="xl"
      nextButtonText={resolveButtonText()}
      submitButtonText={resolveButtonText()}
      isNextButtonLoading={false}
      isSubmitButtonLoading={false}
    />
  );
}
